import type { ApiResponse } from './generic';

/**
 * E-signatures and acknowledgements (docs/product/e-signatures-documenso.md).
 * Mirrors the backend `esignature` module. An acknowledgement is evidence of
 * reading, never called a signature (D72).
 */

export enum DocumentTemplateCategory {
  EmploymentContract = 'employment_contract',
  OfferLetter = 'offer_letter',
  Amendment = 'amendment',
  Policy = 'policy',
  Nda = 'nda',
  Other = 'other',
}

export enum DocumentTemplateMode {
  Signature = 'signature',
  Acknowledgement = 'acknowledgement',
  /** Fill in and submit, no signature, no "I have read" (2026-10-10). */
  Form = 'form',
}

export enum DocumentLanguage {
  Fr = 'fr',
  En = 'en',
}

export enum ManualFieldType {
  Text = 'text',
  Number = 'number',
  Money = 'money',
  Date = 'date',
  LongText = 'long_text',
  /** One choice among the author's options; recipient fields only. */
  Dropdown = 'dropdown',
  /** Several choices; recipient fields only. */
  MultiSelect = 'multi_select',
  /** Ticked or not (a required one must be ticked); recipient fields only. */
  Checkbox = 'checkbox',
}

/** Types only the recipient fills in. */
export const RECIPIENT_ONLY_FIELD_TYPES: readonly ManualFieldType[] = [
  ManualFieldType.Dropdown,
  ManualFieldType.MultiSelect,
  ManualFieldType.Checkbox,
];

/** Types with an options list. */
/** A form question can be a full sentence (Modulmax, 2026-10-29). */
export const MANUAL_FIELD_LABEL_MAX = 500;
export const MANUAL_OPTION_LABEL_MAX = 250;

export const CHOICE_FIELD_TYPES: readonly ManualFieldType[] = [
  ManualFieldType.Dropdown,
  ManualFieldType.MultiSelect,
];

/** Who fills a field in: HR when sending, or the recipient. */
export type ManualFieldFiller = 'sender' | 'recipient';

/**
 * Where a document field may be saved in the employee file (2026-10-10).
 * Mirror of the backend catalogue (`esignature/merge/profile-targets.util.ts`);
 * a company's own fields are `custom.<id>`. `clothing.<column>` (before
 * 2026-10-10) reads as `employee.<column>`. Pay, SIN, banking, the login
 * email, role, status, manager, department, job title and branch are never
 * targets. Work information typed by the employee waits for HR.
 */
export enum ProfileTargetGroup {
  Personal = 'personal',
  Address = 'address',
  Emergency = 'emergency',
  Family = 'family',
  Sizes = 'sizes',
  Work = 'work',
  Custom = 'custom',
}

/** How a target's value is checked: which document field types fit it. */
export enum ProfileValueKind {
  Text = 'text',
  /** A clothing size: text, a choice or a number ("36"). */
  Size = 'size',
  Phone = 'phone',
  Email = 'email',
  Date = 'date',
  Gender = 'gender',
  Relation = 'relation',
  Language = 'language',
  Country = 'country',
  Province = 'province',
  PostalCode = 'postal_code',
}

/** Emergency contact slots: update the primary one, or add new ones. */
export const EMERGENCY_CONTACT_SLOTS = ['primary', 'new1', 'new2', 'new3'];
export const FAMILY_MEMBER_SLOTS = ['new1', 'new2', 'new3'];

/**
 * The parts of an emergency contact, and of a family member. The company
 * portal builds the full target list from these (`profileTargets.ts`).
 */
export const CONTACT_PARTS: [string, ProfileValueKind][] = [
  ['first_name', ProfileValueKind.Text],
  ['last_name', ProfileValueKind.Text],
  ['phone', ProfileValueKind.Phone],
  ['relation', ProfileValueKind.Relation],
  ['email', ProfileValueKind.Email],
];
export const FAMILY_PARTS: [string, ProfileValueKind][] = [
  ...CONTACT_PARTS,
  ['date_of_birth', ProfileValueKind.Date],
  ['gender', ProfileValueKind.Gender],
];

/** Targets written before 2026-10-10, as they read today. */
export const LEGACY_PROFILE_TARGETS: Record<string, string> = {
  'clothing.shirt_size': 'employee.shirt_size',
  'clothing.pants_size': 'employee.pants_size',
  'clothing.pants_length': 'employee.pants_length',
};

/** Which document field types each kind of value takes (as the API checks). */
export const PROFILE_KIND_ACCEPTS: Record<ProfileValueKind, ManualFieldType[]> =
  {
    [ProfileValueKind.Text]: [ManualFieldType.Text, ManualFieldType.Dropdown],
    [ProfileValueKind.Size]: [
      ManualFieldType.Text,
      ManualFieldType.Dropdown,
      ManualFieldType.Number,
    ],
    [ProfileValueKind.Phone]: [ManualFieldType.Text],
    [ProfileValueKind.Email]: [ManualFieldType.Text],
    [ProfileValueKind.Date]: [ManualFieldType.Date],
    [ProfileValueKind.Gender]: [ManualFieldType.Dropdown, ManualFieldType.Text],
    [ProfileValueKind.Relation]: [
      ManualFieldType.Dropdown,
      ManualFieldType.Text,
    ],
    [ProfileValueKind.Language]: [
      ManualFieldType.Dropdown,
      ManualFieldType.Text,
    ],
    [ProfileValueKind.Country]: [
      ManualFieldType.Text,
      ManualFieldType.Dropdown,
    ],
    [ProfileValueKind.Province]: [
      ManualFieldType.Text,
      ManualFieldType.Dropdown,
    ],
    [ProfileValueKind.PostalCode]: [ManualFieldType.Text],
  };

/** The stored choices of a choice kind, for "use this field's options". */
export const PROFILE_KIND_CHOICES: Partial<Record<ProfileValueKind, string[]>> =
  {
    [ProfileValueKind.Gender]: ['male', 'female', 'other', 'prefer_not_to_say'],
    [ProfileValueKind.Relation]: [
      'spouse',
      'son',
      'daughter',
      'mother',
      'father',
      'sibling',
      'friend',
      'other',
    ],
    [ProfileValueKind.Language]: ['fr', 'en'],
  };

/** What a mapped answer did to the employee file once completed. */
export type ProfileUpdateStatus =
  'saved' | 'failed' | 'pending' | 'approved' | 'rejected';

/** Why an answer was not saved (`esignature` `profile_issue.<reason>`). */
export type ProfileIssueReason =
  | 'invalid_value'
  | 'phone_invalid'
  | 'email_invalid'
  | 'postal_code_invalid'
  | 'date_invalid'
  | 'choice_unknown'
  | 'country_unknown'
  | 'province_unknown'
  | 'too_long'
  | 'name_required'
  | 'field_unavailable'
  | 'write_failed';

/** One mapped field of a completed document, as HR reads it. */
export interface ProfileUpdateView {
  key: string;
  label: string;
  target: string;
  status: ProfileUpdateStatus;
  reason: ProfileIssueReason | null;
  message: string | null;
  /** Pending only: what approving writes. */
  value: string | null;
  decidedAt: string | null;
}

/** Where an HR decision applies (path segment of the decision route). */
export enum ProfileUpdateSource {
  SignatureRequest = 'signature-requests',
  Acknowledgement = 'acknowledgements',
}

/** One choice of a dropdown or multi-select field. */
export interface ManualFieldOption {
  value: string;
  labelFr: string;
  labelEn: string;
}

/**
 * Every placeholder a template may hold, written `{{<value>}}` in the body.
 * Mirror of the backend catalogue (`esignature/merge/merge-tokens.ts`); a
 * `{{manual.<key>}}` token is declared per template instead.
 */
export enum DocumentMergeToken {
  EmployeeFirstName = 'employee.first_name',
  EmployeeLastName = 'employee.last_name',
  EmployeeFullName = 'employee.full_name',
  EmployeeEmail = 'employee.email',
  EmployeeJobTitle = 'employee.job_title',
  EmployeeDepartment = 'employee.department',
  EmployeeLocation = 'employee.location',
  EmployeeLocationAddress = 'employee.location_address',
  EmployeeStartDate = 'employee.start_date',
  EmployeeEmploymentType = 'employee.employment_type',
  EmployeeProbationEndDate = 'employee.probation_end_date',
  EmployeeContractEndDate = 'employee.contract_end_date',
  EmployeeNumber = 'employee.employee_number',
  EmployeeManagerName = 'employee.manager_name',
  EmployeeManagerJobTitle = 'employee.manager_job_title',
  /** Personal details: read only with `employee.read`, else typed by HR. */
  EmployeeAddress = 'employee.address',
  EmployeeCity = 'employee.city',
  EmployeeProvince = 'employee.province',
  EmployeePostalCode = 'employee.postal_code',
  EmployeeCountry = 'employee.country',
  EmployeeFullAddress = 'employee.full_address',
  EmployeePhone = 'employee.phone',
  EmployeePersonalEmail = 'employee.personal_email',
  EmployeeBirthDate = 'employee.birth_date',
  CandidateFirstName = 'candidate.first_name',
  CandidateLastName = 'candidate.last_name',
  CandidateFullName = 'candidate.full_name',
  CandidateEmail = 'candidate.email',
  CandidateJobTitle = 'candidate.job_title',
  CandidateLocation = 'candidate.location',
  CandidateStartDate = 'candidate.start_date',
  CompensationSalary = 'compensation.salary',
  CompensationSalaryAmount = 'compensation.salary_amount',
  CompensationPayFrequency = 'compensation.pay_frequency',
  CompensationPayType = 'compensation.pay_type',
  /** Only when the salary is paid that way; never converted. */
  CompensationHourlyRate = 'compensation.hourly_rate',
  CompensationAnnualSalary = 'compensation.annual_salary',
  CompanyName = 'company.name',
  CompanyLegalName = 'company.legal_name',
  CompanyAddress = 'company.address',
  /** The admin picked to sign for the company. */
  CompanySignatoryName = 'company.signatory_name',
  DocumentDate = 'document.date',
  SignatureRecipient = 'signature.recipient',
  SignatureCompany = 'signature.company',
  DateSignedRecipient = 'date_signed.recipient',
  DateSignedCompany = 'date_signed.company',
  InitialsRecipient = 'initials.recipient',
}

/** The picker value for "the recipient's direct manager" (dynamic). */
export const DIRECT_MANAGER_SIGNATORY = 'direct_manager';

/**
 * What happens before the employee gets a document (2026-10-15): nothing,
 * one chosen person approves, or the company signatory signs first.
 */
export type BeforeEmployeeStep = 'none' | 'approve' | 'sign';

/** Who signs for the company, as a preview resolved it. */
export interface DocumentSignatoryView {
  choice: 'direct_manager' | 'employee' | 'admin';
  name: string | null;
  viaDirectManager: boolean;
  /** "Direct manager", and this person has none: the send is refused. */
  missingManager: boolean;
  /** Not someone who can sign for this company. */
  invalid: boolean;
}

/** Error codes the backend sends in `errorCode`. */
export enum EsignatureErrorCode {
  BodyRequired = 'BODY_REQUIRED',
  UnknownTokens = 'UNKNOWN_TOKENS',
  UndeclaredManualFields = 'UNDECLARED_MANUAL_FIELDS',
  InvalidManualFields = 'INVALID_MANUAL_FIELDS',
  SignatureTokensInAcknowledgement = 'SIGNATURE_TOKENS_IN_ACKNOWLEDGEMENT',
  UnresolvedFields = 'UNRESOLVED_FIELDS',
  SalaryAccessRequired = 'SALARY_ACCESS_REQUIRED',
  TemplateRequired = 'TEMPLATE_REQUIRED',
  BroadcastNeedsAcknowledgement = 'BROADCAST_NEEDS_ACKNOWLEDGEMENT',
  BroadcastPersonalTokens = 'BROADCAST_PERSONAL_TOKENS',
  AudienceGroupRequired = 'AUDIENCE_GROUP_REQUIRED',
  AudienceEmpty = 'AUDIENCE_EMPTY',
  DueDatePast = 'DUE_DATE_PAST',
  RemindedRecently = 'REMINDED_RECENTLY',
  BroadcastClosed = 'BROADCAST_CLOSED',
  BroadcastOutsideScope = 'BROADCAST_OUTSIDE_SCOPE',
  AcknowledgementExpired = 'ACKNOWLEDGEMENT_EXPIRED',
  ContentChanged = 'CONTENT_CHANGED',
  ConfirmationRequired = 'CONFIRMATION_REQUIRED',
  ManualValueTooLong = 'MANUAL_VALUE_TOO_LONG',
  SignatoryManagerMissing = 'SIGNATORY_MANAGER_MISSING',
  RecipientAnswersRequired = 'RECIPIENT_ANSWERS_REQUIRED',
  RecipientAnswerInvalid = 'RECIPIENT_ANSWER_INVALID',
  AnswersRequired = 'ANSWERS_REQUIRED',
}

/** Who a broadcast reaches (the reminder audience model, D69). */
export enum BroadcastAudienceScope {
  All = 'all',
  Org = 'org',
  Employee = 'employee',
}

/** One person's copy of a document to read. Never a signature (D72). */
export enum AcknowledgementStatus {
  Pending = 'pending',
  Acknowledged = 'acknowledged',
  /** HR closed the broadcast before this person confirmed. */
  Expired = 'expired',
}

/**
 * A value HR types at send time. Its labels are for the HR sender, in their
 * portal language; the document only shows the typed value.
 */
export interface ManualFieldDefinition {
  key: string;
  labelEn: string;
  labelFr: string;
  type: ManualFieldType;
  required: boolean;
  /**
   * Prefilled at send time, editable per send. Long text: sanitized rich
   * HTML (paragraphs, lists, bold, italic).
   */
  defaultValue?: string;
  /** Absent = 'sender' (HR types it at send time). */
  filledBy?: ManualFieldFiller;
  options?: ManualFieldOption[];
  helpFr?: string;
  helpEn?: string;
  /**
   * Saved on completion to this employee file target (`PROFILE_TARGETS`
   * or `custom.<custom field id>`); the file's value also prefills it.
   */
  profileField?: string;
}

/** A recipient field as the person filling it in reads it. */
export interface RecipientFieldView {
  key: string;
  label: string;
  type: ManualFieldType;
  required: boolean;
  help: string | null;
  options: { value: string; label: string }[];
  savesToProfile: boolean;
}

/** One answer: text, the chosen option value(s), or a tick. */
export type RecipientAnswerValue = string | string[] | boolean;
export type RecipientAnswers = Record<string, RecipientAnswerValue>;

/** An answer as the sender reads it. */
export interface RecipientAnswerView {
  key: string;
  label: string;
  value: string | null;
  type: ManualFieldType;
  /** HR's view: the employee file target and what the answer did there. */
  profileField?: string | null;
  profileUpdate?: {
    status: ProfileUpdateStatus;
    reason: ProfileIssueReason | null;
    message: string | null;
    decidedAt: string | null;
  } | null;
}

export interface DocumentTemplate {
  id: string;
  companyId: string;
  name: string;
  category: DocumentTemplateCategory;
  mode: DocumentTemplateMode;
  /** One language per template: the document's (2026-10-04). */
  language: DocumentLanguage;
  bodyHtml: string | null;
  manualFields: ManualFieldDefinition[];
  documentTypeId: string | null;
  /** Who signs and in which order (`recipient`, `company_signatory`). */
  signerRoles?: { role: 'recipient' | 'company_signatory'; order: number }[];
  /** Legacy: an admin default saved before 2026-10-10 (still honoured). */
  defaultCompanySignatoryAdminId?: string | null;
  /**
   * The default company signatory: an employee id, `direct_manager` (the
   * recipient's manager, resolved per document) or null.
   */
  defaultCompanySignatory?: string | null;
  defaultApproverAdminId?: string | null;
  requiresApproval: boolean;
  /** Before the employee (2026-10-15), read from approval and signer order. */
  beforeEmployee?: BeforeEmployeeStep;
  /** The default approver: a company admin id, `direct_manager` or null. */
  defaultApprover?: string | null;
  /** Tell the employee's direct manager when sent and when signed. */
  notifyManager?: boolean;
  expiryDays: number;
  reminderDays: number[];
  version: number;
  isActive: boolean;
  /** Tokens used in the body. */
  tokens: string[];
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplatePayload {
  name: string;
  category: DocumentTemplateCategory;
  mode: DocumentTemplateMode;
  language: DocumentLanguage;
  bodyHtml: string | null;
  manualFields: ManualFieldDefinition[];
  /**
   * Prefilled as company signatory at send time: an active employee of this
   * company who can sign, or `direct_manager`; null clears it.
   */
  defaultCompanySignatory?: string | null;
  documentTypeId?: string | null;
  requiresApproval?: boolean;
  beforeEmployee?: BeforeEmployeeStep;
  /** A company admin id or `direct_manager`; null clears it. */
  defaultApprover?: string | null;
  notifyManager?: boolean;
  expiryDays?: number;
}

export interface DocumentTemplateVersion {
  version: number;
  createdAt: string;
  createdBy: string | null;
}

export type DocumentSalarySource = 'profile' | 'manual';

export interface DocumentSalaryInput {
  source: DocumentSalarySource;
  amount?: number;
  currency?: string;
  payType?: string;
}

export interface DocumentPreviewPayload {
  templateId?: string;
  draft?: Pick<
    DocumentTemplatePayload,
    'mode' | 'language' | 'bodyHtml' | 'manualFields'
  >;
  /** The employee the document is for; or `applicationId` (ATS offer). */
  employeeId?: string;
  /** ATS offer: the candidate's application (needs recruiting.read). */
  applicationId?: string;
  /** ATS offer: the start date offered (YYYY-MM-DD). */
  startDate?: string;
  salary?: DocumentSalaryInput;
  manualValues?: Record<string, string>;
  /** Values typed for profile fields that are empty for this person. */
  fieldValues?: Record<string, string>;
  /** The company signatory, for `{{company.signatory_name}}`. */
  companySignatoryAdminId?: string;
  /** An employee id or `direct_manager`; wins over the admin id and default. */
  companySignatory?: string;
  /** The step picked for this send (`approve` resolves the approver). */
  beforeEmployee?: BeforeEmployeeStep;
  /** A company admin id or `direct_manager`. */
  approver?: string;
}

/**
 * A profile value a document needs that came out empty for this person:
 * not on file (`missing`) or not the sender's to read (`no_access`). The
 * composer asks for it, like a manual field.
 */
export interface DocumentFieldGap {
  token: string;
  type: 'text' | 'date' | 'money';
  reason: 'missing' | 'no_access';
}

export interface DocumentPreview {
  html: string;
  /** The template's (or the draft's) language. */
  language: DocumentLanguage;
  unresolved: string[];
  unknownTokens: string[];
  missingRequired: string[];
  /** Profile values to type for this person (before any typed value). */
  fieldGaps?: DocumentFieldGap[];
  salary: {
    used: boolean;
    source: DocumentSalarySource | null;
    canReadProfile: boolean;
    /** null when the caller may not know. */
    hasProfileSalary: boolean | null;
    /**
     * The profile salary only starts after today (a new hire, a dated
     * raise): the day it starts, which the document still uses. Null
     * otherwise, or when the caller may not know.
     */
    profileSalaryStartsOn?: string | null;
  };
  employee: { id: string; name: string };
  /** ATS offer previews: the candidate. */
  candidate?: { applicationId: string; name: string } | null;
  /** Who signs for the company on this document, as the send resolves it. */
  signatory?: DocumentSignatoryView | null;
  /** The direct manager who would approve, as the send resolves it. */
  approver?: DocumentSignatoryView | null;
}

export interface BroadcastAudience {
  audienceScope: BroadcastAudienceScope;
  branchIds?: string[];
  departmentIds?: string[];
  designationIds?: string[];
  employeeIds?: string[];
}

export interface CreateBroadcastPayload extends BroadcastAudience {
  /** A template, or an uploaded PDF (`uploadedPdfKey`), phase 4. */
  templateId?: string;
  uploadedPdfKey?: string;
  uploadedPdfName?: string;
  /** For information or for signature; required with an uploaded PDF. */
  mode?: DocumentTemplateMode;
  /** The language of an uploaded PDF. */
  language?: DocumentLanguage;
  /** For signature: typed fields, the same value for everyone. */
  manualValues?: Record<string, string>;
  companySignatoryAdminId?: string;
  /** An employee id or `direct_manager` (each recipient's own manager). */
  companySignatory?: string;
  title?: string;
  includeNewHires?: boolean;
  dueDate?: string | null;
}

/** A PDF uploaded to send (`POST esignature/uploads`). */
export interface UploadedEsignaturePdf {
  uploadKey: string;
  fileName: string;
  pages: number;
  sha256: string;
  size: number;
}

export interface BroadcastCounts {
  total: number;
  acknowledged: number;
  pending: number;
  expired: number;
}

export interface DocumentBroadcast {
  id: string;
  title: string;
  templateId: string | null;
  templateVersion: number | null;
  mode: DocumentTemplateMode;
  audienceScope: BroadcastAudienceScope;
  /** `YYYY-MM-DD` or null. */
  dueDate: string | null;
  includeNewHires: boolean;
  createdAt: string;
  closedAt: string | null;
  lastRemindedAt: string | null;
  createdBy: string | null;
  /** Within the reader's branches. */
  counts: BroadcastCounts;
  branchIds?: string[];
  departmentIds?: string[];
  designationIds?: string[];
  employeeIds?: string[];
  /** Phase 4: an uploaded PDF, its language, the announcement it came from. */
  uploadedPdfName?: string | null;
  language?: DocumentLanguage | null;
  announcementId?: string | null;
  flowTaskId?: string | null;
  /** People fill fields in: the tracking table shows their answers. */
  hasFields?: boolean;
}

/** Where one person stands on a broadcast for signature. */
export type RecipientSignatureStatus =
  | 'not_sent'
  | 'pending'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'failed';

export interface BroadcastRecipient {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  department: string | null;
  jobTitle: string | null;
  location: string | null;
  /** A read status, or a signature status on a broadcast for signature. */
  status: AcknowledgementStatus | RecipientSignatureStatus;
  language: DocumentLanguage | null;
  version: number | null;
  contentSha256: string | null;
  sentAt: string | null;
  firstViewedAt: string | null;
  acknowledgedAt: string | null;
  ip: string | null;
  userAgent: string | null;
  remindedAt: string | null;
  /** Broadcasts for signature: the person's signing request. */
  requestId?: string | null;
  signedAt?: string | null;
  declinedAt?: string | null;
  /** What the person filled in, once submitted. */
  answers?: RecipientAnswerView[] | null;
}

/** The employee's own copy (`employee/acknowledgements`). */
export interface MyAcknowledgement {
  id: string;
  broadcastId: string;
  title: string;
  status: AcknowledgementStatus;
  language: DocumentLanguage;
  version: number | null;
  contentSha256: string;
  dueDate: string | null;
  sentAt: string;
  firstViewedAt: string | null;
  acknowledgedAt: string | null;
  companyName: string | null;
  /**
   * Sent by an onboarding / offboarding step. My Tasks lists the document in
   * place of that step's own row (`flowTaskId` is the step's task id).
   */
  fromFlowStep?: boolean;
  flowTaskId?: string | null;
  /** Read and confirm, or a form to fill in and submit. */
  mode?: DocumentTemplateMode;
  hasFields?: boolean;
  /** Only on the single-document read. */
  contentHtml?: string;
  /** The fields to fill in, in the reader's language. */
  fields?: RecipientFieldView[];
  /** First values: the profile's, else the template's default; or the answers. */
  values?: RecipientAnswers;
  /** Once submitted. */
  answers?: RecipientAnswerView[] | null;
  /** An uploaded PDF to read instead (a short-lived link), phase 4. */
  pdfUrl?: string | null;
}

export type GetBroadcastsResponse = ApiResponse<DocumentBroadcast[]>;
export type GetBroadcastResponse = ApiResponse<DocumentBroadcast>;
export type GetBroadcastRecipientsResponse = ApiResponse<BroadcastRecipient[]>;
export type GetAudienceCountResponse = ApiResponse<{
  count: number;
  /** The people the audience would have reached but cannot (D163). */
  unreachable: { count: number; employees: { id: string; name: string }[] };
}>;
export type GetMyAcknowledgementsResponse = ApiResponse<MyAcknowledgement[]>;
export type GetMyAcknowledgementResponse = ApiResponse<MyAcknowledgement>;
export type GetMyAcknowledgementSummaryResponse = ApiResponse<{
  pending: number;
}>;

export type GetDocumentTemplatesResponse = ApiResponse<DocumentTemplate[]>;
export type GetDocumentTemplateResponse = ApiResponse<DocumentTemplate>;
export type GetDocumentTemplateVersionsResponse = ApiResponse<
  DocumentTemplateVersion[]
>;
export type GetDocumentPreviewResponse = ApiResponse<DocumentPreview>;
